import { Table as TanstackTable } from '@tanstack/react-table';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { ButtonWithIcon } from 'src/components/Buttons';
import { FormatContent } from 'src/contexts/ContentContext';
import { PosTextField } from 'src/core/POS/PosTextField';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { ChevronEndIconV2 } from 'src/svgs/Viagogo/goicons/ChevronEndIconV2';
import { ChevronLeftIconV3 } from 'src/svgs/Viagogo/goicons/ChevronLeftIconV3';
import { ChevronRightIconV3 } from 'src/svgs/Viagogo/goicons/ChevronRightIconV3';
import { ChevronStartIconV2 } from 'src/svgs/Viagogo/goicons/ChevronStartIconV2';
import { FormatContentId } from 'src/utils/constants/formatContentId';

import { usePagination, UsePaginationOptions } from '../hooks/usePagination';
import { TablePaginationVariant } from '../types';
import * as styles from './TablePagination.css';

// Navigation button component
interface NavigationButtonProps {
  icon: React.ReactNode;
  onClick: () => void;
  disabled: boolean;
  ariaLabel: string;
}

const NavigationButton = ({
  icon,
  onClick,
  disabled,
  ariaLabel,
}: NavigationButtonProps) => (
  <ButtonWithIcon
    className={styles.navigationButton}
    variant={'icon'}
    size={'unset'}
    textColor={'icon'}
    shape={'rect'}
    onClick={onClick}
    icon={icon}
    disabled={disabled}
    aria-label={ariaLabel}
  />
);

export interface TablePaginationProps<T = any> extends UsePaginationOptions {
  table: TanstackTable<T>;
  className?: string;
  variant?: TablePaginationVariant;
  totalCount?: number | null;
}

export function TablePagination<T = any>({
  table,
  pageSize: defaultPageSize = 10,
  className,
  variant = 'default',
  totalCount,
  ...paginationOptions
}: TablePaginationProps<T>) {
  const [inputValue, setInputValue] = useState('');

  const pagination = usePagination(table, {
    pageSize: defaultPageSize,
    ...paginationOptions,
  });

  const {
    currentPage,
    totalPages,
    canPreviousPage,
    canNextPage,
    goToPage,
    previousPage,
    nextPage,
    goToFirstPage,
    goToLastPage,
  } = pagination;

  useEffect(() => {
    setInputValue(currentPage.toString());
  }, [currentPage]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const pageNum = parseInt(value, 10);

    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
      setInputValue(value);
      goToPage(pageNum);
    }
  };

  return (
    <div className={clsx(styles.paginationContainer, className)}>
      <div className={styles.paginationControls}>
        <NavigationButton
          onClick={goToFirstPage}
          disabled={!canPreviousPage}
          ariaLabel="Go to first page"
          icon={
            <ChevronStartIconV2
              size={vars.iconSize.s}
              width={'12px'}
              height={'12px'}
            />
          }
        />

        <NavigationButton
          onClick={previousPage}
          disabled={!canPreviousPage}
          ariaLabel="Go to previous page"
          icon={
            <ChevronLeftIconV3
              size={vars.iconSize.s}
              fill={vars.color.iconPrimary}
              width={'12px'}
              height={'12px'}
            />
          }
        />

        <div className={styles.pageIndicator}>
          {variant === 'input' ? (
            <Stack alignItems="center" gap="s">
              <PosTextField
                type={'number'}
                value={inputValue}
                onChange={handleInputChange}
                alignment={'center'}
                min={1}
                max={totalPages}
                className={styles.pageInputField}
                rootProps={{
                  className: styles.pageInputRoot,
                }}
              />
              <div className={styles.pageIndicatorText}>
                <FormatContent
                  id={FormatContentId.OfValue}
                  params={`${totalPages}`}
                />
              </div>
            </Stack>
          ) : (
            <div className={styles.pageDisplayText}>
              <FormatContent
                id={FormatContentId.PageOfPages}
                params={[`${currentPage}`, `${totalPages}`]}
              />
            </div>
          )}
        </div>

        <NavigationButton
          onClick={nextPage}
          disabled={!canNextPage}
          ariaLabel="Go to next page"
          icon={
            <ChevronRightIconV3
              size={vars.iconSize.s}
              fill={vars.color.iconPrimary}
              width={'12px'}
              height={'12px'}
            />
          }
        />

        <NavigationButton
          onClick={goToLastPage}
          disabled={!canNextPage}
          ariaLabel="Go to last page"
          icon={
            <ChevronEndIconV2
              size={vars.iconSize.s}
              fill={vars.color.iconPrimary}
              width={'12px'}
              height={'12px'}
            />
          }
        />
      </div>

      {totalCount != null && (
        <div className={styles.totalCountText}>
          {totalCount.toLocaleString()} results
        </div>
      )}
    </div>
  );
}
